import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { axiosInstance } from '@/utils/axios-instance'

export const useRootStore = defineStore('root', () => {
  const loading = ref(false)
  const user = ref<any>(null)
  const subscriptions = ref<any>(null)
  const itemsToShow = ref(8)
  const currentSlide = ref<any>(null)
  const currentDiagram = ref<any>(null)
  const boughtSlide = ref<any>(null)
  const categories = ref<any[]>([])
  const tags = ref<string[]>([])
  const debugEnabled = ref(false)
  const mobileMenuActive = ref(false)
  const buyMoreCreditsDisplayed = ref(false)

  const normalizedSubscriptions = computed(() => {
    if (Array.isArray(subscriptions.value)) return subscriptions.value
    if (Array.isArray(subscriptions.value?.results)) return subscriptions.value.results
    if (Array.isArray(user.value?.subscriptions)) return user.value.subscriptions
    return []
  })

  const activeSubscriptions = computed(() =>
    normalizedSubscriptions.value.filter((sub: any) => ['active', 'new'].includes(sub?.status))
  )

  const isSubscriber = computed(() => activeSubscriptions.value.length > 0)

  const hasUnlimitedSubscription = computed(() =>
    activeSubscriptions.value.some((sub: any) => sub.plan?.is_unlimited_credits)
  )

  const isLoading = computed(() => loading.value)

  const getCreditsAmount = computed(() => {
    if (!user.value) return null
    if (hasUnlimitedSubscription.value) return 'Unlimited'
    return user.value.credits ?? 0
  })

  const showSearchDebug = computed(() => debugEnabled.value && internalUser.value)

  const internalUser = computed(() => {
    try {
      return user.value?.email?.split('@')[1] === 'infodiagram.com'
    } catch {
      return false
    }
  })

  const shouldDisplayBuyMoreCredits = computed(() =>
    !buyMoreCreditsDisplayed.value && user.value?.credits < 2 && !hasUnlimitedSubscription.value
  )

  function setLoading(v: boolean) { loading.value = v }
  function setSubscriptions(s: any) { subscriptions.value = s }
  function setItemsToShow(n: number) { itemsToShow.value = n }
  function setCreditsAmount(n: number) { if (user.value) user.value.credits = n }
  function setCurrentSlide(s: any) { currentSlide.value = s }
  function setCurrentDiagram(d: any) { currentDiagram.value = d }
  function setBoughtSlide(s: any) { boughtSlide.value = s }
  function setCategories(c: any[]) { categories.value = c }
  function setTags(t: string[]) { tags.value = t }
  function setDebug(flag: boolean) { debugEnabled.value = flag }
  function toggleMobileMenu() { mobileMenuActive.value = !mobileMenuActive.value }
  function setBuyMoreCreditsDisplayed(flag: boolean) { buyMoreCreditsDisplayed.value = flag }

  function spendCredits(amount: number) {
    if (user.value) user.value.credits -= amount
  }

  async function getUser(force = false) {
    if (user.value !== null && !force) return
    try {
      const response = await axiosInstance.get('/me/')
      user.value = response.data
    } catch (error) {
      console.log(error)
    }
  }

  async function getSubscriptions() {
    try {
      const response = await axiosInstance.get('/subscriptions/')
      subscriptions.value = response.data
    } catch (error) {
      console.log(error)
    }
  }

  async function fetchCategories() {
    const controller = new AbortController()
    const response = await axiosInstance.get('/categories/', { params: { parent: true }, signal: controller.signal })
    categories.value = response.data.results
  }

  async function fetchTags() {
    const controller = new AbortController()
    const response = await axiosInstance.get('/tags/', { signal: controller.signal })
    tags.value = response.data.results.map((t: any) => t.tag)
  }

  async function fetchSpecificDiagram({ diagramName, slideNumber }: { diagramName: string; slideNumber: string | null }) {
    try {
      loading.value = true

      // Resolve the diagram from Typesense (the same source browsing uses) so the
      // returned shape matches what Browse.vue renders and the gallery expects.
      const { buildPublicClient, mapTypesenseDiagramHit } = await import('@/utils/typesense-client')
      const client = buildPublicClient()
      const result = await client.collections('diagrams_en').documents().search({
        q: '*',
        query_by: 'name,title',
        filter_by: `name:=${diagramName}`,
        per_page: 1,
        page: 1
      })

      const hit = result.hits?.[0]
      if (!hit) throw new Error(`Diagram not found: ${diagramName}`)
      const diagram = mapTypesenseDiagramHit(hit)

      const { useBrowsingStore } = await import('@/stores/useBrowsingStore')
      const browsingStore = useBrowsingStore()
      browsingStore.setItems([diagram])
      // Deep link shows only this diagram; stop the infinite loader from paging
      // more diagrams in on top of it once it re-mounts.
      browsingStore.setHasMore(false)
      currentDiagram.value = true

      if (slideNumber !== null) {
        const parsedSlide = parseInt(String(slideNumber), 10)
        const slideIndex = diagram.slides.findIndex((s: any) => s.number === parsedSlide)
        if (slideIndex !== -1) {
          currentSlide.value = { diagramId: diagram.id, slideNumber: slideIndex }
        }
      }
      return diagram
    } catch (error) {
      console.error('Error fetching specific diagram:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  return {
    loading, user, subscriptions, itemsToShow, currentSlide, currentDiagram, boughtSlide,
    categories, tags, debugEnabled, mobileMenuActive, buyMoreCreditsDisplayed,
    normalizedSubscriptions, activeSubscriptions, isSubscriber, hasUnlimitedSubscription, isLoading, getCreditsAmount, showSearchDebug,
    internalUser, shouldDisplayBuyMoreCredits,
    setLoading, setSubscriptions, setItemsToShow, setCreditsAmount, setCurrentSlide,
    setCurrentDiagram, setBoughtSlide, setCategories, setTags, setDebug,
    toggleMobileMenu, setBuyMoreCreditsDisplayed, spendCredits,
    getUser, getSubscriptions, fetchCategories, fetchTags, fetchSpecificDiagram
  }
})
